import { type TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import styled from 'styled-components';

import { useTypedParams } from '@sorare/routing';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { NeonEdge } from '@sorare/core/src/atoms/ui/NeonEdge';
import { TextureBackground } from '@sorare/core/src/atoms/ui/TextureBackground';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { COMPETITION_COLORS, isHexColor } from '@sorare/core/src/lib/colors';
import { isType } from '@sorare/core/src/lib/gql';
import { isRarityStr } from '@sorare/core/src/lib/so5';

import { CollectionSlotsPreview } from 'components/myCards/LeagueCollectionPreview/CollectionSlotsPreview';
import { LeaguePageHeader } from 'components/myCards/LeaguePageHeader';
import { LeagueSearchInput } from 'components/myCards/LeagueSearchInput';

import type {
  LeagueSlugPageQuery,
  LeagueSlugPageQueryVariables,
} from './__generated__/page.graphql';
import type { PageParams } from './__generated__/routeParams';

const Root = styled.div`
  isolation: isolate;
`;

const Header = styled.div`
  position: relative;
  isolation: isolate;
`;

const TextureBackgroundWrapper = styled.div`
  position: absolute;
  inset: 0;
  z-index: -1;
  & > * {
    width: 100%;
    height: 100%;
  }
  &:after {
    background: linear-gradient(
      180deg,
      rgba(var(--c-rgb-black), 0) 50%,
      var(--c-black) 100%
    );
    content: '';
    position: absolute;
    inset: 0;
  }
`;

const CollectionsList = styled(Vertical)`
  padding: 0 var(--double-unit);
`;

const SlotPreviewWrapper = styled.div`
  border: 1px solid var(--c-nd-50);
  border-radius: var(--unit);
  overflow: hidden;
`;

const InnerSlotPreviewWrapper = styled.div`
  padding: var(--double-unit);
`;

const StyledTextureBackground = styled(TextureBackground)`
  position: absolute;
  inset: 0;
  z-index: -1;
`;

const Divider = styled.div`
  height: 1px;
  background: var(--c-nd-150);
  margin: var(--double-unit);
`;

const LEAGUE_SLUG_PAGE_QUERY = gql`
  query LeagueSlugPageQuery(
    $leagueSlug: String!
    $seasonStartYear: Int!
    $rarity: Rarity!
    $setSlug: String!
    $userSlug: String!
  ) {
    currentUser {
      slug
      cardCollections(
        cardSetSlug: $setSlug
        seasonStartYears: [$seasonStartYear]
        rarities: [$rarity]
        forCompetitions: [$leagueSlug]
        first: 50
      ) {
        nodes {
          slug
          anyTeam {
            slug
            name
            shortName
            styleGuideColor
            ... on Club {
              slug
              domesticLeague {
                slug
                displayName
              }
            }
          }
          ...CollectionSlotsPreview_cardCollection
          ...LeaguePageHeader_cardCollection
        }
      }
    }
  }
  ${CollectionSlotsPreview.fragments.cardCollection}
  ${LeaguePageHeader.fragments.cardCollection}
` as TypedDocumentNode<LeagueSlugPageQuery, LeagueSlugPageQueryVariables>;

const LeagueSlugPageComponent = ({ rarity }: { rarity: Rarity }) => {
  const { currentUser } = useCurrentUserContext();

  const { league: leagueSlug, set: setSlug } = useTypedParams<PageParams>();
  const { data } = useQuery(LEAGUE_SLUG_PAGE_QUERY, {
    variables: {
      leagueSlug,
      seasonStartYear: 2025,
      rarity,
      setSlug,
      userSlug: currentUser?.slug || '',
    },
  });
  const { nodes: cardCollections } = data?.currentUser?.cardCollections || {};
  const anyTeam = cardCollections?.at(0)?.anyTeam;
  const competition = isType(anyTeam, 'Club') ? anyTeam.domesticLeague : null;
  const [search, setSearch] = useState('');

  const filterBySearchInput = (
    cardCollection: NonNullable<
      NonNullable<
        NonNullable<LeagueSlugPageQuery['currentUser']>['cardCollections']
      >['nodes']
    >[number]
  ) => {
    return (
      !search.trim() ||
      [cardCollection.anyTeam?.shortName, cardCollection.anyTeam?.name].some(
        token => token?.toLowerCase().includes(search.trim().toLowerCase())
      )
    );
  };
  return (
    <>
      <PageHeader text={competition?.displayName}></PageHeader>
      <TextureBackgroundWrapper>
        <TextureBackground
          background="wave2"
          mode="dark"
          color={COMPETITION_COLORS[leagueSlug] || 'var(--c-nd-150)'}
        />
      </TextureBackgroundWrapper>
      <Root>
        <Header>
          <LeaguePageHeader cardCollections={cardCollections} />
          <LeagueSearchInput
            onChange={setSearch}
            collectionsCount={cardCollections?.length || 0}
          />
          <Divider />
        </Header>
        <CollectionsList gap={2}>
          {cardCollections?.filter(filterBySearchInput).map(cardCollection => {
            const collectionColor = isHexColor(
              cardCollection.anyTeam?.styleGuideColor
            )
              ? cardCollection.anyTeam?.styleGuideColor
              : 'var(--c-nd-150)';
            return (
              <SlotPreviewWrapper key={cardCollection.slug}>
                <NeonEdge
                  color={collectionColor}
                  side="top"
                  spread="40%"
                  showHalo="always"
                >
                  <StyledTextureBackground
                    background="wave2"
                    mode="dark"
                    color={collectionColor}
                  />
                  <InnerSlotPreviewWrapper>
                    <CollectionSlotsPreview
                      key={cardCollection.slug}
                      cardCollection={cardCollection}
                    />
                  </InnerSlotPreviewWrapper>
                </NeonEdge>
              </SlotPreviewWrapper>
            );
          })}
        </CollectionsList>
      </Root>
    </>
  );
};

const LeagueSlugPage = () => {
  const { rarity } = useTypedParams<PageParams>();

  if (!isRarityStr(rarity)) {
    return null;
  }
  return <LeagueSlugPageComponent rarity={rarity} />;
};
export default LeagueSlugPage;
